import styled from '@emotion/styled/macro';
import { motion } from 'framer-motion';
import { loadProject, selectProjectBySlug } from 'modules/projects/projectsSlice';
import { FC, useEffect, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { mq } from 'styles/breakpoints';
import ProjectBox from './ProjectBox';
import ProjectHeader from './ProjectHeader';
import CorporateProjectHeader from './Corporate/ProjectHeader';
import CertificateSlider from './CertificateSlider';
import ProjectBlurb from './ProjectBlurb';
import ProjectDescription from './ProjectDescription';
import ProjectClose from './ProjectClose';
import SDGContent from 'components/SDG/SDGContent';
import ScrollableWithGradient from 'components/ScrollableWithGradient/ScrollableWithGradient';
import useBreakpoint from 'hooks/useBreakpoint';
import ProjectDescriptionAccordion from './ProjectDescriptionAccordion';
import { isCorporateThemeSelector } from 'modules/app/appSlice';
import { Flex, Box, Button } from '@chakra-ui/react';
import { ArrowLeft } from 'components/Icons/Icons';
import { IconButton } from 'components';
import { useTranslation } from 'react-i18next';
import { hideZendeskButton } from 'utils/zendeskUtils';

const Wrapper = styled(motion.div)`
  display: flex;
  border-radius: 9px;
  position: relative;
  flex-direction: column;

  ${mq.lg} {
    flex-direction: row;
    margin: 0;
  }
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  left: 10px;
  top: -10px;
  z-index: 2;
`;

const CloseButtonMobile = styled(Button)`
  position: fixed;
  bottom: 10px;
  right: 15px;
  width: 40%;
  z-index: 2;
  max-width: 260px;
  justify-content: flex-start;
  & > span:first-of-type.chakra-button__icon {
    margin-right: calc(50% - 30px);
  }
`;

const Background = styled(Box)`
  position: absolute;
  width: calc(100% - 20px);
  height: calc(100% - 20px);
  left: 10px;
  top: 20px;
  bottom: 0;
  z-index: -1;
  border-radius: 15px;
  ${mq.lg} {
    top: -20px;
    height: calc(100% + 20px);
  }
`;

const Project: FC = () => {
  const history = useHistory();
  const location = useLocation();
  //@ts-ignore
  const { id } = useParams();
  const project = useSelector(selectProjectBySlug(id as string));
  const dispatch = useDispatch();
  const isCorporateTheme = useSelector(isCorporateThemeSelector);
  const { t } = useTranslation('projects');
  const { isTablet, isMobile } = useBreakpoint();

  //@ts-ignore
  const isStandalone = !location?.state?.from;

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!project) {
      dispatch(loadProject(id as string));
    }

    isStandalone && hideZendeskButton();
  }, [id, project]);

  const onCloseIconClick = (): void => history.goBack();

  return (
    <>
      <Wrapper initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
        {isCorporateTheme ? (
          <>
            <Flex flexDirection="column" mt="20px" mr={[0, 0, 0, '25px']} w={['100%', null, null, null, 'calc(50% - 12.5px)']}>
              <Box mb="10px">
                <ProjectBox boxWidth="smd" pos="relative" zIndex={2} mr={0} maxW="100%" sx={{ mr: '0px !important', maxW: '100% !important' }}>
                  <CorporateProjectHeader project={project} />
                </ProjectBox>
              </Box>
              <ProjectBox
                boxWidth="smd"
                pos="relative"
                maxH={['auto', null, null, null, '655px']}
                h={['auto', null, null, null, '655px']}
                zIndex={2}
                sx={{ mr: '0px !important', maxW: '100% !important' }}
              >
                <SDGContent sdgs={project?.sdgs ?? []} short={isTablet} />
              </ProjectBox>
            </Flex>
            <Flex
              flexDirection="column"
              pt="20px"
              w={['100%', null, null, null, 'calc(50% - 12.5px)']}
              pos={['relative', null, null, null, 'absolute']}
              right={[null, null, null, null, 0]}
              height={[null, null, null, null, '100%']}
              overflowY={[null, null, null, null, 'hidden']}
            >
              <Box mb="10px">
                <ProjectBox
                  boxWidth="smd"
                  pos="relative"
                  overflowY={[null, null, null, null, 'hidden']}
                  zIndex={2}
                  sx={{ mr: '0px !important', maxW: '100% !important' }}
                >
                  <CertificateSlider project={project} />
                </ProjectBox>
              </Box>
              <ProjectBox
                boxWidth="smd"
                pos="relative"
                h={['auto', null, null, null, 'inherit']}
                overflowY={[null, null, null, null, 'hidden']}
                zIndex={2}
                sx={{ mr: '0px !important', maxW: '100% !important' }}
              >
                <ScrollableWithGradient maxH={['auto', null, null, null, null]} height="inherit">
                  <ProjectBlurb project={project} />
                  {isMobile ? <ProjectDescriptionAccordion project={project} /> : <ProjectDescription project={project} />}
                </ScrollableWithGradient>
              </ProjectBox>
            </Flex>
            {!isStandalone && (isTablet || isMobile) ? (
              <CloseButtonMobile minWidth="150px" onClick={onCloseIconClick} leftIcon={<ArrowLeft />}>
                <span>{t('back')}</span>
              </CloseButtonMobile>
            ) : (
              <CloseButton aria-label="Close project's full info page" icon={<ArrowLeft />} onClick={onCloseIconClick} />
            )}
          </>
        ) : (
          <>
            <ProjectBox includeRightMargin boxWidth="lg" maxH="1300px" h="1300px" pos="relative" zIndex={2}>
              <ProjectHeader project={project} />
              <CertificateSlider project={project} />
              <SDGContent sdgs={project?.sdgs ?? []} short={isTablet} />
            </ProjectBox>
            <ProjectBox boxWidth={isCorporateTheme ? 'smd' : 'md'} maxH="1300px" pos="relative" zIndex={1} mb={['55px', null, null, null, 0]}>
              <ScrollableWithGradient maxH="1238px">
                <ProjectBlurb project={project} />
                {isMobile ? <ProjectDescriptionAccordion project={project} /> : <ProjectDescription project={project} />}
              </ScrollableWithGradient>
            </ProjectBox>
            {!isStandalone && <ProjectClose onCloseIconClick={onCloseIconClick} />}
          </>
        )}
      </Wrapper>
      {isCorporateTheme && <Background backgroundColor="secondary.100" />}
    </>
  );
};

export default Project;
